import {
  ApplicationConfig, provideBrowserGlobalErrorListeners, Provider, EnvironmentProviders,
  isDevMode, Injectable, provideAppInitializer, inject, importProvidersFrom,
} from '@angular/core';

import {
  provideRouter, Router, withComponentInputBinding, withDisabledInitialNavigation, withInMemoryScrolling
} from '@angular/router';

import { routes } from './app.routes';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideTransloco, provideTranslocoMissingHandler, TranslocoConfig, TranslocoMissingHandler } from '@jsverse/transloco';
import { environment } from './app.environment';
import { initializeDependencies } from './app.initializer';
import { TranslocoHttpLoader } from './core/utils/transloco-loader';
import { AssetService, FontSizeService, LanguageService, provideServices, ThemeService } from './services/services.module';
import { provideAuthentication } from './auth';
import { provideDataLayer } from './data-layer';
import { logInfo } from 'in-time-logger';
import { provideFirebase } from './core/firebase';
import { AppStore, provideAppStore } from './app.store';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { provideLocalizedDataHelper } from './core/utils/localized-data-helper';
import { httpInterceptor } from './core/interceptors/http-interceptor';
import { provideUnsavedChangesProtection } from './ui/unsaved-changes';

@Injectable()
export class DefaultMissingHandler implements TranslocoMissingHandler {
  handle(key: string, config: TranslocoConfig) {
    if(config.missingHandler.logMissingKey) {
      const msg = `Missing translation for '${key}'`;
      // eslint-disable-next-line no-console
      console.warn(`%c ${msg}`, 'font-size: 12px; color: red');
    }

    return config.prodMode ? '?' : key;
  }
}

function getAppProviders(): Array<Provider | EnvironmentProviders> {
  const providers: Array<Provider | EnvironmentProviders> = [];

  providers.push(provideBrowserGlobalErrorListeners());
  providers.push(importProvidersFrom(MatSnackBarModule));
  providers.push(provideUnsavedChangesProtection());

  providers.push(provideRouter(
    routes,
    withComponentInputBinding(),
    withDisabledInitialNavigation(),
    withInMemoryScrolling({
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled',
    }),
  ));

  providers.push(provideHttpClient(
    withFetch(),
    withInterceptors([
      httpInterceptor,
    ])
  ));

  providers.push(provideTransloco({
    config: {
      availableLangs: [ 'en', 'hu', 'ro' ],
      defaultLang: environment.defaultLanguage,
      reRenderOnLangChange: true,
      prodMode: !isDevMode(),
    },
    loader: TranslocoHttpLoader
  }));

  providers.push(provideTranslocoMissingHandler(DefaultMissingHandler));

  providers.push(provideFirebase());
  providers.push(provideAuthentication());
  providers.push(provideDataLayer());
  providers.push(provideAppStore());
  providers.push(provideLocalizedDataHelper());
  providers.push(provideServices());

  providers.push(provideAppInitializer(async () => {
    const languageService = inject(LanguageService);
    const fontSizeService = inject(FontSizeService);
    const themeService = inject(ThemeService);
    const assetService = inject(AssetService);
    const appStore = inject(AppStore);
    const router = inject(Router);

    logInfo('App is initializing...');

    initializeDependencies();

    assetService.load();
    themeService.load();
    fontSizeService.load();
    languageService.load();

    await appStore.checkLogin();
    logInfo('App has been initialized.');

    router.initialNavigation();
  }));

  return providers;
}

export function getBrowserConfig(): ApplicationConfig {
  return {
    providers: getAppProviders(),
  };
}
